import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import AdvancedChartScreen from '../AdvancedChartScreen';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import { SnackbarVariant } from '../../components/Snackbar';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import ResearchGrowthTables from './ResearchGrowthTables';
import ResearchGrowthFiscalTables from './ResearchGrowthFiscalTables';

const BACK_TESTING_SYNC_FROM_CHARTS_KEY = 'garp_backtesting_sync_from_charts_v1';

interface ResearchBackTestingScreenProps {
  isDarkMode: boolean;
  isAdmin?: boolean;
  onToggleTheme: () => void;
  onBackToMain: () => void;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  financialsSource?: 'fmp' | 'fiscal';
  authToken?: string;
  authUserId?: number | null;
  authUsername?: string | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const ResearchBackTestingScreen: React.FC<ResearchBackTestingScreenProps> = (props) => {
  // For now, Back Testing intentionally reuses the exact same chart UI as the Charts tab.
  // It also shows the Growth tables beneath the chart for convenience.
  const [visibleRange, setVisibleRange] = useState<{ startKey: string; endKey: string } | null>(null);
  const [visiblePeriodType, setVisiblePeriodType] = useState<'Annual Data' | 'Quarterly Data' | 'TTM'>('Annual Data');
  const [backTestingSeedReady, setBackTestingSeedReady] = useState(false);
  const pendingVisibleRangeRef = useRef<{ startKey: string; endKey: string } | null>(null);
  const visibleRangeCommitTimerRef = useRef<number | null>(null);
  const resolvedFinancialsSource = props.financialsSource ?? 'fmp';
  const showSourceLabels = Boolean(props.isAdmin);
  const sourceLabelText = resolvedFinancialsSource === 'fiscal' ? 'fiscal.ai' : 'FMP';

  const canPersist = !!props.authToken;
  const resolvedUserId =
    typeof props.authUserId === 'number' &&
    Number.isFinite(props.authUserId) &&
    (props.authUserId as number) > 0
      ? (props.authUserId as number)
      : undefined;

  const bookmarkTitle = useMemo(() => {
    // Keep a stable title so the bookmark "filled" state is intuitive.
    return 'Back Testing Tables';
  }, []);

  const [savingBookmark, setSavingBookmark] = useState(false);
  const [didBookmark, setDidBookmark] = useState(false);
  const [bookmarkFlash, setBookmarkFlash] = useState(false);
  const bookmarkFlashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (bookmarkFlashTimeoutRef.current) {
        try {
          window.clearTimeout(bookmarkFlashTimeoutRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const triggerBookmarkFlash = () => {
    setBookmarkFlash(true);
    if (bookmarkFlashTimeoutRef.current) {
      try {
        window.clearTimeout(bookmarkFlashTimeoutRef.current);
      } catch {
        // ignore
      }
    }
    bookmarkFlashTimeoutRef.current = window.setTimeout(() => {
      setBookmarkFlash(false);
    }, 30_000);
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!canPersist) {
        setDidBookmark(false);
        return;
      }

      // Reset immediately on stock/title change so we don't show a filled icon
      // from the previous stock while loading.
      setDidBookmark(false);

      try {
        const items = await adminApi.listResearchDeskScreenshots(props.authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Back Testing',
          stockSymbol: props.selectedStock,
          title: bookmarkTitle,
          limit: 10,
        });
        if (cancelled) return;
        setDidBookmark(items.length > 0);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          props.onUnauthorized?.();
          return;
        }
        if (cancelled) return;
        setDidBookmark(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canPersist, bookmarkTitle, props.authToken, resolvedUserId, props.onUnauthorized, props.selectedStock]);

  const toUTCKey = (d: Date): string => {
    return d.toISOString().slice(0, 10);
  };

  const subtractYearsUtcKey = (endKey: string, yearsBack: number): string | undefined => {
    const key = String(endKey ?? '').trim();
    if (!key) return undefined;
    const ts = Date.parse(`${key}T00:00:00.000Z`);
    if (!Number.isFinite(ts)) return undefined;
    const d = new Date(ts);
    return new Date(Date.UTC(d.getUTCFullYear() - yearsBack, d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
  };

  const tablesRangeStartKey = useMemo(() => {
    const endKey = String(visibleRange?.endKey ?? '').trim();
    if (!endKey) return undefined;
    return subtractYearsUtcKey(endKey, 10);
  }, [visibleRange?.endKey]);

  const tablesSelectedPeriod = useMemo<'annual' | 'quarter'>(() => {
    return visiblePeriodType === 'Annual Data' ? 'annual' : 'quarter';
  }, [visiblePeriodType]);

  useEffect(() => {
    return () => {
      if (visibleRangeCommitTimerRef.current) {
        try {
          window.clearTimeout(visibleRangeCommitTimerRef.current);
        } catch {
          // ignore
        }
        visibleRangeCommitTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      let shouldSyncFromCharts = false;
      try {
        shouldSyncFromCharts = typeof window !== 'undefined' && window.sessionStorage.getItem(BACK_TESTING_SYNC_FROM_CHARTS_KEY) === '1';
      } catch {
        shouldSyncFromCharts = false;
      }

      if (!shouldSyncFromCharts) {
        setBackTestingSeedReady(true);
        return;
      }

      setBackTestingSeedReady(false);

      const symbol = String(props.selectedStock ?? '').trim().toUpperCase();
      const rawUsername = String(props.authUsername ?? '').trim().toLowerCase();
      const userKey =
        typeof props.authUserId === 'number' && Number.isFinite(props.authUserId) && props.authUserId > 0
          ? `uid:${props.authUserId}`
          : (rawUsername ? `user:${rawUsername}` : 'anon');

      const seedBackTestingFromCharts = () => {
        if (typeof window === 'undefined' || !symbol) return;

        const storageVersion = 1;
        const chartsKey = `advanced-chart:prefs:Charts:v${storageVersion}:${userKey}:${symbol}`;
        const backTestingKey = `advanced-chart:prefs:Back Testing:v${storageVersion}:${userKey}:${symbol}`;

        try {
          const raw = window.localStorage.getItem(chartsKey);
          if (!raw) return;
          const parsed = JSON.parse(raw) as Record<string, unknown>;
          const withOverrides = {
            ...parsed,
            elementVisibilityMode: 'overrides',
          };
          window.localStorage.setItem(backTestingKey, JSON.stringify(withOverrides));
        } catch {
          // ignore storage errors
        }
      };

      try {
        if (typeof window !== 'undefined' && symbol) {
          const backTestingKey = `advanced-chart:prefs:Back Testing:v1:${userKey}:${symbol}`;
          window.localStorage.removeItem(backTestingKey);
        }
      } catch {
        // ignore storage errors
      }

      seedBackTestingFromCharts();

      if (props.authToken && symbol) {
        try {
          await adminApi.deleteMyResearchChartsState(props.authToken, `${symbol}::BACK_TESTING`);
        } catch (e: any) {
          if (isUnauthorized(e)) {
            props.onUnauthorized?.();
            return;
          }
        }
      }

      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(BACK_TESTING_SYNC_FROM_CHARTS_KEY);
        }
      } catch {
        // ignore storage errors
      }

      if (!cancelled) setBackTestingSeedReady(true);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [props.selectedStock, props.authToken, props.authUserId, props.authUsername, props.onUnauthorized]);

  const tablesWrapperStyle = useMemo<React.CSSProperties>(() => {
    return {
      marginTop: '50px',
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '100%',
      padding: '0 2.5rem',
    };
  }, []);

  const tablesFrameStyle = useMemo<React.CSSProperties>(() => {
    return {
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box',
      border: `1px solid ${props.isDarkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: 12,
      background: props.isDarkMode ? '#111827' : '#ffffff',
      padding: '12px 12px 16px 12px',
    };
  }, [props.isDarkMode]);

  const bookmarkButtonStyle = useMemo<React.CSSProperties>(() => {
    return {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 28,
      height: 28,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      border: `1px solid ${props.isDarkMode ? '#374151' : '#e5e7eb'}`,
      background: props.isDarkMode ? '#0b1220' : '#ffffff',
      color: props.isDarkMode ? '#e5e7eb' : '#111827',
      cursor: 'pointer',
      opacity: savingBookmark ? 0.6 : 1,
    };
  }, [props.isDarkMode, savingBookmark]);

  const toDataUrlWithFallback = (canvas: HTMLCanvasElement, type: string, quality: number) => {
    try {
      const dataUrl = canvas.toDataURL(type, quality);
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) return { type, dataUrl };
    } catch {
      // ignore
    }
    const fallbackType = 'image/png';
    return { type: fallbackType, dataUrl: canvas.toDataURL(fallbackType) };
  };

  const makeThumbDataUrl = async (canvas: HTMLCanvasElement, maxWidthPx: number, quality: number): Promise<string> => {
    const srcW = canvas.width;
    const srcH = canvas.height;
    if (!srcW || !srcH) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    const scale = Math.min(1, maxWidthPx / srcW);
    const dstW = Math.max(1, Math.floor(srcW * scale));
    const dstH = Math.max(1, Math.floor(srcH * scale));

    const thumb = document.createElement('canvas');
    thumb.width = dstW;
    thumb.height = dstH;
    const ctx = thumb.getContext('2d');
    if (!ctx) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;
    ctx.drawImage(canvas, 0, 0, dstW, dstH);
    return toDataUrlWithFallback(thumb, 'image/webp', quality).dataUrl;
  };

  const captureTables = async (): Promise<HTMLCanvasElement | null> => {
    const el = document.getElementById('research-backtesting-tables-capture');
    if (!el) return null;

    const bg = window.getComputedStyle(el).backgroundColor;
    const backgroundColor = !bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)' ? '#ffffff' : bg;

    // Keep canvas under typical browser limits (tables can be very tall).
    const width = Math.max(1, (el as HTMLElement).scrollWidth || el.clientWidth || 1);
    const height = Math.max(1, (el as HTMLElement).scrollHeight || el.clientHeight || 1);
    const maxCanvasDim = 15000;
    const scaleCap = maxCanvasDim / Math.max(width, height);
    const scale = Math.max(0.5, Math.min(2, scaleCap || 1));

    try {
      return await html2canvas(
        el,
        {
          backgroundColor,
          scale,
          useCORS: true,
          logging: false,
        } as any
      );
    } catch {
      return null;
    }
  };

  const saveTablesBookmark = async () => {
    if (!canPersist) {
      props.onNotify?.('Please login to save screenshots.', 'error');
      return;
    }
    if (savingBookmark) return;

    // While the icon is in the 30s "saved" state, ignore repeat clicks.
    // After it returns to default, allow saving again.
    if (bookmarkFlash) return;

    setSavingBookmark(true);
    try {
      const captured = await captureTables();
      if (!captured) {
        props.onNotify?.('Tables are not ready to capture yet.', 'error');
        return;
      }

      const full = toDataUrlWithFallback(captured, 'image/webp', 0.92);
      const thumbDataUrl = await makeThumbDataUrl(captured, 1100, 0.82);

      await adminApi.createResearchDeskScreenshot(props.authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Back Testing',
        stockSymbol: props.selectedStock,
        title: bookmarkTitle,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      setDidBookmark(true);
      triggerBookmarkFlash();
      void `${props.selectedStock} • ${bookmarkTitle} • Screenshot saved`;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        props.onUnauthorized?.();
        return;
      }
      props.onNotify?.(e?.message || 'Failed saving screenshot', 'error');
    } finally {
      setSavingBookmark(false);
    }
  };

  return (
    <div id="research-backtesting-screen" style={{ display: 'flex', flexDirection: 'column' }}>
      <style>{`
        /* Back Testing only: make embedded Growth tables fit the chart+right-panel width. */
        #research-backtesting-screen #growth-tables-embedded {
          padding: 0 !important;
          border-top: 0 !important;
        }
      `}</style>

      {backTestingSeedReady && (
        <AdvancedChartScreen
          key={`${props.selectedStock}:seed-ready`}
          isDarkMode={props.isDarkMode}
          isAdmin={props.isAdmin}
          onToggleTheme={props.onToggleTheme}
          onBackToMain={props.onBackToMain}
          selectedStock={props.selectedStock}
          authToken={props.authToken}
          authUserId={props.authUserId ?? null}
          authUsername={props.authUsername ?? null}
          onUnauthorized={props.onUnauthorized}
          onNotify={props.onNotify}
          disableMinHeight
          // Use the exact same Charts scope so navigation behavior (prev/next
          // quarter/year) is 1:1 with the Charts tab.
          prefsScope="Charts"
          dataScope={resolvedFinancialsSource === 'fmp' ? 'Charts fpm' : 'Charts'}
          visibilityScope="Charts"
          onVisibleRangeChange={(start, end) => {
            const startKey = toUTCKey(start);
            const endKey = toUTCKey(end);
            const nextRange = { startKey, endKey };
            const pending = pendingVisibleRangeRef.current;
            if (pending?.startKey === nextRange.startKey && pending?.endKey === nextRange.endKey) return;
            pendingVisibleRangeRef.current = nextRange;
            if (visibleRangeCommitTimerRef.current) return;

            visibleRangeCommitTimerRef.current = window.setTimeout(() => {
              visibleRangeCommitTimerRef.current = null;
              const latest = pendingVisibleRangeRef.current;
              if (!latest) return;
              setVisibleRange((prev) => (
                prev?.startKey === latest.startKey && prev?.endKey === latest.endKey
                  ? prev
                  : latest
              ));
            }, 150);
          }}
          onPeriodTypeChange={(type) => {
            setVisiblePeriodType((prev) => (prev === type ? prev : type));
          }}
        />
      )}

      {resolvedFinancialsSource === 'fiscal' && (
      <div style={tablesWrapperStyle}>
        <div style={tablesFrameStyle}>
          <button
            type="button"
            aria-label="Bookmark"
            title="Bookmark"
            style={bookmarkButtonStyle}
            disabled={savingBookmark}
            onClick={saveTablesBookmark}
          >
              <img
                src={bookmarkFlash ? '/images/bookmark_fill.svg' : (props.isDarkMode ? '/images/darkmode/bookmark_empy.svg' : '/images/bookmark_empy.svg')}
                alt="Bookmark"
                style={{ width: 18, height: 18 }}
              />
          </button>

          <div id="research-backtesting-tables-capture">
            {resolvedFinancialsSource === 'fiscal' ? (
              <ResearchGrowthFiscalTables
                isDarkMode={props.isDarkMode}
                selectedStock={props.selectedStock}
                selectedPeriod={tablesSelectedPeriod}
                mode="embedded"
                asOfDate={visibleRange?.endKey ?? undefined}
                // Back Testing: align tables to the current visible range.
                // Always render a rolling 10-year window relative to the right-edge date.
                rangeStart={tablesRangeStartKey}
                showSourceLabels={showSourceLabels}
              />
            ) : (
              <ResearchGrowthTables
                isDarkMode={props.isDarkMode}
                selectedStock={props.selectedStock}
                selectedPeriod={tablesSelectedPeriod}
                financialsSource={resolvedFinancialsSource}
                mode="embedded"
                asOfDate={visibleRange?.endKey ?? undefined}
                // Back Testing: align tables to the current visible range.
                // Always render a rolling 10-year window relative to the right-edge date.
                rangeStart={tablesRangeStartKey}
                calcTitleSuffix={showSourceLabels ? sourceLabelText : ''}
                analystTitleSuffix=""
                showSourceLabel={showSourceLabels}
                sourceLabel={sourceLabelText}
                selectedPeriodType={visiblePeriodType}
              />
            )}
          </div>
        </div>
      </div>
      )}

      <div
        style={{
          padding: '0 2.5rem 1rem 2.5rem',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <DataSourceAttribution isDarkMode={props.isDarkMode} style={{ marginTop: 0 }} />
      </div>
    </div>
  );
};

export default ResearchBackTestingScreen;
